/**
 * A question, a model, and what the model was allowed to see.
 *
 * Laid out the way a chat is expected to be: the conversation down the middle,
 * the composer at the bottom, and the choices *on* the composer rather than in
 * a settings pane — a `+` for files and how hard to search the web, and the
 * model in the corner, because both are things you change per question.
 *
 * Two kinds of key can answer: the service's own, and any Groq connection the
 * person made in Connections. The model picker lists both under their own
 * headings. The same goes for search — a Tavily connection, or the service's
 * key — and the `+` menu says which is doing it.
 *
 * The thread is held here and sent whole each turn. The provider is stateless,
 * and keeping a copy on the server would only create a second version of the
 * conversation to disagree with this one. Attached files work the same way:
 * their text lives in this tab and travels with the turns it belongs to.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorNotice, Loading } from '@/shared/components'
import {
  ModelPicker,
  type PickerGroup,
  type PickerModel,
  providerName,
} from '@/shared/components/ModelPicker'
import { useToast } from '@/shared/components/toast'
import { Composer, Dots, FileIcon, MODES } from '@/shared/components/Composer'
import { Markdown } from '@/shared/components/Markdown'
import { useChatOptions, useConnectionModels, useReadFile, useSendChat } from './hooks'
import type {
  ChatAnswer,
  ChatAttachment,
  ChatMode,
  ChatSearch,
  ChatSource,
} from './types'
import './chat.css'

interface Turn {
  role: 'user' | 'assistant'
  content: string
  /** Files that went with the question. */
  attachments?: string[]
  /** Which model answered, and what it cost. Absent on your own messages. */
  model?: string
  durationMs?: number
  tokens?: number
  reasoningTokens?: number | null
  cappedTo?: number | null
  /** What the model thought on the way to the answer, when it showed its work. */
  thinking?: string | null
  mode?: ChatMode
  sources?: ChatSource[]
  search?: ChatSearch | null
  error?: boolean
}

/** Where a model comes from: the service key, or one connection. */
type SourceKey = 'service' | string


const REMEMBER = 'chat.choices'

interface Remembered {
  source?: SourceKey
  model?: string
  mode?: ChatMode
  searchVia?: string
}

function remembered(): Remembered {
  try {
    return JSON.parse(localStorage.getItem(REMEMBER) ?? '{}') as Remembered
  } catch {
    return {}
  }
}

export function ChatPage() {
  const options = useChatOptions()
  const send = useSendChat()
  const readFile = useReadFile()
  const toast = useToast()

  const [source, setSource] = useState<SourceKey>(() => remembered().source ?? 'service')
  const [model, setModel] = useState(() => remembered().model ?? '')
  const [mode, setMode] = useState<ChatMode>(() => remembered().mode ?? 'chat')
  // 'service' for the deployment's TAVILY_API_KEY, else a Tavily connection id.
  const [searchVia, setSearchVia] = useState<string>(() => remembered().searchVia ?? '')
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const [draft, setDraft] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const [reading, setReading] = useState(0)
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const llmConnections = options.data?.llm_connections ?? []
  const searchConnections = options.data?.search_connections ?? []
  const connectionModels = useConnectionModels(llmConnections.map((c) => c.connection_id))

  /** Every model the page can offer, grouped by whose key calls it. */
  const groups = useMemo(() => {
    const out: PickerGroup[] = []
    if (options.data) {
      out.push({
        key: 'service',
        label: 'Service key',
        sub: options.data.providers?.length ? options.data.providers.map(providerName).join(' + ') : null,
        models: options.data.models,
        error: options.data.models_configured
          ? options.data.models_error
          : 'Neither GROQ_API_KEY nor HF_TOKEN is set on the API.',
      })
    }
    llmConnections.forEach((c, i) => {
      const q = connectionModels[i]
      out.push({
        key: c.connection_id,
        label: c.label,
        sub: `your ${providerName(c.connector_id)} key`,
        models: q?.data?.models ?? [],
        error: q?.data?.error ?? (q?.error ? String((q.error as Error).message) : null),
        loading: q?.isLoading,
      })
    })
    return out
  }, [options.data, llmConnections, connectionModels])

  const current = groups.find((g) => g.key === source)
  const chosen: PickerModel | undefined = current?.models.find((m) => m.model === model)
  const anyModel = groups.some((g) => g.models.length > 0)

  // Land on something that answers, rather than making the first act of using
  // the page a decision about a list you have not seen yet. A remembered
  // choice wins when it still exists.
  useEffect(() => {
    if (chosen) return
    const first = groups.find((g) => g.key === source && g.models.length) ?? groups.find((g) => g.models.length)
    if (first) {
      setSource(first.key)
      setModel(first.models[0].model)
    }
  }, [groups, chosen, source])

  const serverSearch = options.data?.web_search.server_key ?? false
  const searchChoices = useMemo(
    () => [
      ...searchConnections.map((c) => ({ id: c.connection_id, label: c.label, sub: 'Tavily connection' })),
      ...(serverSearch ? [{ id: 'service', label: 'Service key', sub: 'TAVILY_API_KEY on the API' }] : []),
    ],
    [searchConnections, serverSearch],
  )
  useEffect(() => {
    if (!searchChoices.length) return
    if (!searchChoices.some((c) => c.id === searchVia)) setSearchVia(searchChoices[0].id)
  }, [searchChoices, searchVia])
  const canSearch = searchChoices.length > 0

  useEffect(() => {
    try {
      localStorage.setItem(REMEMBER, JSON.stringify({ source, model, mode, searchVia }))
    } catch {
      /* private mode; nothing to remember into */
    }
  }, [source, model, mode, searchVia])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns.length, send.isPending])

  // --- files ------------------------------------------------------------------

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files)
      if (!list.length) return
      if (attachments.length + list.length > 8) {
        toast.error(new Error('Up to 8 files can be attached to one question.'))
        return
      }
      setReading((n) => n + list.length)
      for (const file of list) {
        try {
          const read = await readFile.mutateAsync(file)
          setAttachments((prev) =>
            prev.some((a) => a.name === read.name) ? prev : [...prev, read],
          )
        } catch (error) {
          toast.error(error)
        } finally {
          setReading((n) => n - 1)
        }
      }
      inputRef.current?.focus()
    },
    [attachments.length, readFile, toast],
  )

  // --- asking -------------------------------------------------------------------

  async function ask() {
    const question = draft.trim()
    if (!question || !chosen) return

    const history = [
      ...turns,
      { role: 'user' as const, content: question, attachments: attachments.map((a) => a.name) },
    ]
    setTurns(history)
    setDraft('')
    const sent = attachments
    setAttachments([])

    try {
      const answer = await send.mutateAsync({
        model,
        connection_id: source === 'service' ? null : source,
        mode,
        search_connection_id: mode !== 'chat' && searchVia !== 'service' ? searchVia || null : null,
        attachments: sent.map((a) => ({ name: a.name, text: a.text })),
        messages: history
          .filter((t) => !t.error)
          .map((t) => ({ role: t.role, content: t.content })),
      })
      setTurns((prev) => [...prev, answerTurn(answer, chosen)])
    } catch (error) {
      toast.error(error)
      setTurns((prev) => prev.slice(0, -1))
      setDraft(question)
      setAttachments(sent)
    }
  }

  if (options.isLoading) return <Loading label="Asking which models are available…" />

  const empty = turns.length === 0 && !send.isPending
  const activeMode = MODES.find((m) => m.id === mode)

  const composer = (
    <Composer
      drop={empty ? 'down' : 'up'}
      draft={draft}
      onDraft={setDraft}
      onSend={ask}
      disabled={!chosen}
      pending={send.isPending}
      placeholder={
        chosen
          ? activeMode
            ? `${activeMode.label}: ask ${chosen.label}…`
            : `Ask ${chosen.label}…`
          : 'No model available'
      }
      inputRef={inputRef}
      attachments={attachments}
      reading={reading}
      onRemove={(name) => setAttachments((prev) => prev.filter((a) => a.name !== name))}
      onFiles={addFiles}
      mode={mode}
      onMode={setMode}
      canSearch={canSearch}
      searchChoices={searchChoices}
      searchVia={searchVia}
      onSearchVia={setSearchVia}
      searchConnectorId={options.data?.search_connector_id ?? 'tavily'}
      modelPicker={
        <ModelPicker
          groups={groups}
          source={source}
          model={model}
          onPick={(key, id) => {
            setSource(key)
            setModel(id)
          }}
          connectors={options.data?.llm_connectors ?? []}
          returnTo="/chat"
          drop={empty ? 'down' : 'up'}
        />
      }
    />
  )

  return (
    <div className="cp-screen ch-page">
      <header className="ch-head">
        <h1>Chat</h1>
        {!anyModel && !options.isLoading && <span className="ch-warn">no model can answer</span>}
        <span className="cp-spacer" />
        {turns.length > 0 && (
          <button className="btn btn-sm" onClick={() => setTurns([])}>
            New chat
          </button>
        )}
      </header>

      {options.error && (
        <div className="ch-notice">
          <ErrorNotice error={options.error} onRetry={options.refetch} />
        </div>
      )}

      {options.data && !anyModel && (
        <div className="ch-notice callout callout-warn">
          <p className="small" style={{ margin: 0 }}>
            {options.data.models_error ?? 'Nothing here can answer yet.'}
          </p>
          <p className="small" style={{ margin: '8px 0 0' }}>
            <button className="btn btn-sm" onClick={() => options.refetch()} disabled={options.isFetching}>
              {options.isFetching ? 'Checking…' : 'Check again'}
            </button>{' '}
            <span className="faint">
              Set <code>GROQ_API_KEY</code> or <code>HF_TOKEN</code> on the API, or{' '}
              {(options.data.llm_connectors ?? []).map((c, i, all) => (
                <span key={c.connector_id}>
                  {i > 0 && (i === all.length - 1 ? ' or ' : ', ')}
                  <Link to={`/connections/new/${c.connector_id}?return=%2Fchat`}>
                    connect {c.label}
                  </Link>
                </span>
              ))}{' '}
              with your own key — this page rechecks by itself every 15 seconds.
            </span>
          </p>
        </div>
      )}

      <div className="ch-scroll" data-empty={empty || undefined}>
        {empty ? (
          <div className="ch-hero">
            <div className="ch-herostar" aria-hidden="true">
              ✦
            </div>
            <h2>What can I help with?</h2>
            <p className="muted">
              {chosen
                ? chosen.summary
                : 'Pick a model to start.'}
            </p>
            {composer}
            <div className="ch-hints">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  className="ch-hint"
                  data-on={mode === m.id || undefined}
                  disabled={!canSearch}
                  title={canSearch ? m.hint : 'Connect Tavily to search the web'}
                  onClick={() => setMode(mode === m.id ? 'chat' : m.id)}
                >
                  <span aria-hidden="true">{m.icon}</span> {m.label}
                </button>
              ))}
              <button className="ch-hint" onClick={() => inputRef.current?.focus()} title="Or drop files onto the box">
                <span aria-hidden="true">＋</span> Add files
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="ch-thread">
              {turns.map((turn, index) => (
                <TurnView key={index} turn={turn} />
              ))}

              {send.isPending && (
                <div className="ch-turn" data-role="assistant">
                  <div className="ch-bubble ch-pending">
                    <Dots />
                    {mode === 'research'
                      ? 'Planning searches, reading pages, writing…'
                      : mode === 'deep'
                        ? 'Searching and reading pages…'
                        : mode === 'web'
                          ? 'Searching the web…'
                          : `${chosen?.label ?? 'The model'} is answering…`}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="ch-dock">{composer}</div>
          </>
        )}
      </div>
    </div>
  )
}

function answerTurn(answer: ChatAnswer, chosen: PickerModel): Turn {
  if (!answer.ok) {
    return {
      role: 'assistant',
      content: answer.error ?? 'The model did not answer.',
      error: true,
      mode: answer.mode,
      sources: answer.sources,
      search: answer.search,
    }
  }
  // A reasoning model that spent its whole budget thinking returns nothing.
  // Saying so beats an empty bubble that reads as a broken model.
  const content: string = answer.spent_on_reasoning
    ? `_${chosen.label} used its entire token budget reasoning and had none left to answer. Ask something narrower._`
    : answer.content ?? ''
  return {
    role: 'assistant',
    content,
    model: answer.model,
    durationMs: answer.total_ms ?? answer.duration_ms,
    tokens: answer.completion_tokens ?? undefined,
    reasoningTokens: answer.reasoning_tokens,
    cappedTo: answer.capped_to,
    thinking: answer.thinking,
    mode: answer.mode,
    sources: answer.sources,
    search: answer.search,
  }
}

// --- one turn ---------------------------------------------------------------------

function TurnView({ turn }: { turn: Turn }) {
  const [focused, setFocused] = useState<number | null>(null)
  const cited = useMemo(() => new Set((turn.sources ?? []).map((s) => s.index)), [turn.sources])

  if (turn.role === 'user') {
    return (
      <div className="ch-turn" data-role="user">
        {turn.attachments && turn.attachments.length > 0 && (
          <div className="ch-sentfiles">
            {turn.attachments.map((name) => (
              <span className="ch-filechip" key={name} title={name}>
                <FileIcon /> {name}
              </span>
            ))}
          </div>
        )}
        <div className="ch-bubble">{turn.content}</div>
      </div>
    )
  }

  const searched = turn.search?.ran || turn.search?.error
  return (
    <div className="ch-turn" data-role="assistant" data-error={turn.error || undefined}>
      {/* What was searched, before the answer: it explains where the sources
          came from and, in research mode, what the model decided to look for. */}
      {searched && turn.search && (
        <details className="ch-searchlog" open={Boolean(turn.search.error)}>
          <summary>
            <span aria-hidden="true">{MODES.find((m) => m.id === turn.mode)?.icon ?? '◎'}</span>{' '}
            {turn.search.error
              ? 'Web search did not run'
              : `${MODES.find((m) => m.id === turn.mode)?.label ?? 'Searched'} · ${turn.search.results} page${
                  turn.search.results === 1 ? '' : 's'
                }${turn.search.pages_read ? `, ${turn.search.pages_read} read in full` : ''}`}
          </summary>
          <div className="ch-searchlog-body">
            {turn.search.error && <p className="ch-searcherr">{turn.search.error}</p>}
            {turn.search.queries.length > 0 && (
              <ul>
                {turn.search.queries.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            )}
          </div>
        </details>
      )}

      {/* Folded away by default. A reasoning model's working is worth reading
          deliberately and never worth reading by accident. */}
      {turn.thinking && (
        <details className="ch-reasoning">
          <summary>Thinking</summary>
          <div className="ch-reasoning-body">{turn.thinking}</div>
        </details>
      )}

      <div className="ch-bubble">
        {turn.error ? turn.content : <Markdown text={turn.content} cited={cited} onCite={setFocused} />}
      </div>

      {turn.sources && turn.sources.length > 0 && (
        <div className="ch-sources">
          {turn.sources.map((s) => (
            <SourceCard key={s.index} source={s} focused={focused === s.index} />
          ))}
        </div>
      )}

      {!turn.error && (
        <div className="ch-meta mono">
          {turn.model}
          {turn.durationMs != null && ` · ${(turn.durationMs / 1000).toFixed(1)}s`}
          {turn.tokens != null && ` · ${turn.tokens} tokens`}
          {turn.reasoningTokens ? ` · ${turn.reasoningTokens} thinking` : ''}
          {/* Said rather than hidden: a short answer from a capped model is
              the limit talking, not the model. */}
          {turn.cappedTo ? ` · capped at ${turn.cappedTo} tokens/min` : ''}
        </div>
      )}
    </div>
  )
}

function SourceCard({ source, focused }: { source: ChatSource; focused: boolean }) {
  const ref = useRef<HTMLAnchorElement>(null)
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [focused])
  return (
    <a
      ref={ref}
      className="ch-source"
      data-focused={focused || undefined}
      href={source.url}
      target="_blank"
      rel="noreferrer noopener"
      title={source.excerpt}
    >
      <span className="ch-srcn">{source.index}</span>
      <span className="ch-srcbody">
        <span className="ch-srctitle truncate">{source.title}</span>
        <span className="ch-srchost truncate">
          {source.host}
          {source.full ? ' · read in full' : ''}
        </span>
      </span>
    </a>
  )
}
